import { useState, useEffect, useCallback, useRef } from "react";
import { wrapServiceCall } from "../services/ServiceUtils";
import { API_CONFIG } from "../config";

export const API_BASE_URL = API_CONFIG.BASE_URL;

const ANONYMOUS_SESSION_STORAGE_KEY = "anonSessionId";

const createAnonymousSessionId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `anon-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

export const getAnonymousSessionId = () => {
  try {
    const existing = localStorage.getItem(ANONYMOUS_SESSION_STORAGE_KEY);
    if (existing) {
      return existing;
    }
    const sessionId = createAnonymousSessionId();
    localStorage.setItem(ANONYMOUS_SESSION_STORAGE_KEY, sessionId);
    return sessionId;
  } catch {
    return createAnonymousSessionId();
  }
};

const buildMetaFromStatus = (status, overrides = {}) => {
  const type = status >= 500 ? 'ERROR' : status >= 400 ? 'WARNING' : 'INFO';
  return {
    type,
    httpStatusCode: status,
    ...overrides,
  };
};

export const fetchApi = async (endpoint, options = {}) => {
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    'X-Anonymous-Session': getAnonymousSessionId(),
    ...options.headers
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      credentials: 'include',
      ...options,
      headers,
    });

    if (!response.ok) {
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        try {
          const errorData = await response.json();
          return {
            data: null,
            meta: errorData?.type ? errorData : buildMetaFromStatus(response.status, {
              message: errorData?.message || `Request failed with status ${response.status}`,
              details: errorData?.details,
            })
          };
        } catch (jsonError) {
          return {
            data: null,
            meta: buildMetaFromStatus(response.status, {
              message: `Request failed with status ${response.status}`,
              details: jsonError?.message,
            })
          };
        }
      }

      return {
        data: null,
        meta: buildMetaFromStatus(response.status, {
          message: `Request failed with status ${response.status}`
        })
      };
    }

    const contentType = response.headers.get('content-type');

    if (options.responseType === 'blob') {
      const blob = await response.blob();
      return { data: blob, meta: null };
    }

    if (options.responseType === 'text') {
      const text = await response.text();
      return { data: text, meta: null };
    }

    if (contentType && contentType.includes('application/json') && response.status !== 204) {
      const responseData = await response.json();
      const hasData = responseData && typeof responseData === 'object' && Object.prototype.hasOwnProperty.call(responseData, 'data')
        && Object.prototype.hasOwnProperty.call(responseData, 'meta');
      return hasData ? responseData : { data: responseData, meta: null };
    }

    return { data: response.status === 204 ? null : response.status, meta: null };
  } catch (error) {
    if (error.name === 'AbortError') {
      return null;
    }
    return {
      data: null,
      meta: buildMetaFromStatus(500, {
        message: error.message || 'Network error',
        details: error.cause
      })
    };
  }
};

export const useFetch = (initialEndpoint, initialOptions = {}, autoFetch = true) => {
   const endpointRef = useRef(initialEndpoint);
   const optionsRef = useRef(initialOptions);

   const [isLoading, setIsLoading] = useState(!!initialEndpoint && autoFetch);
   const [data, setData] = useState();
   const [error, setError] = useState();

   const abortControllerRef = useRef();

   const fetchData = useCallback(async () => {
      const currentEndpoint = endpointRef.current;
      const currentOptions = optionsRef.current;

      if (!currentEndpoint) return;

      setIsLoading(true);
      setError(null);

      if (abortControllerRef.current) {
         abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      const isFormData = typeof FormData !== 'undefined' && currentOptions.body instanceof FormData;
      const headers = {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        'X-Anonymous-Session': getAnonymousSessionId(),
        ...currentOptions.headers
      };

      try {
         const result = await wrapServiceCall(() =>
            fetchApi(currentEndpoint, {
               credentials: 'include',
               ...currentOptions,
               headers,
               signal: abortControllerRef.current.signal,
            })
         );

         setData(result?.data ?? null);
         setError(result?.meta ?? null);
      } finally {
         setIsLoading(false);
      }
   }, []);

   useEffect(() => {
      if (autoFetch && endpointRef.current) {
         fetchData();
      }

      return () => {
         if (abortControllerRef.current) {
            abortControllerRef.current.abort();
         }
      };
   }, [fetchData, autoFetch]);

   const updateCallProperties = useCallback((newEndpoint, newOptions = {}, fetchImmediately = true) => {
      endpointRef.current = newEndpoint;
      optionsRef.current = { ...optionsRef.current, ...newOptions };

      if (fetchImmediately) {
         fetchData();
      }
   }, [fetchData]);

   const refetch = useCallback(() => {
      fetchData();
   }, [fetchData]);

   const setDataToEmpty = useCallback((newDataState) => {
     setData(newDataState);
   }, []);

   const clearError = useCallback(() => {
      setError(null);
   }, []);

   return [{ data, isLoading, error }, updateCallProperties, refetch, setDataToEmpty, clearError];
};

export default useFetch;
